import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  LayoutGroup,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
} from "framer-motion";
import { color, ease, font } from "../../tokens";
import { STATIC_MODE } from "../../primitives";
import AnimatedIcon from "../../icons";
import {
  ENGAGEMENTS,
  EVENTS,
  RUN_DONE,
  SHIP_AT,
  THRESHOLDS,
  TICKETS,
  Ticket,
  fmtClock,
  progressAt,
  stateOf,
  stepFor,
} from "./missionData";

/**
 * The mission-control board — a scroll-scrubbed kanban run. Scroll
 * position IS the simulation clock: tickets stream from Backlog through
 * In progress and Review into Done, gates stamp, engagements collapse
 * into shipped stamps, and the whole run plays backwards on scroll-up.
 * Visually quotes the real Team Orchestrator dashboard, re-skinned in
 * the site's ink/paper/pink language.
 */

/* ------------------------------------------------------- dark UI tokens */

const ui = {
  panel: "rgba(244, 240, 232, 0.045)",
  panel2: "rgba(244, 240, 232, 0.08)",
  border: "rgba(244, 240, 232, 0.13)",
  borderStrong: "rgba(244, 240, 232, 0.26)",
  text: color.paper,
  muted: color.mutedOnInk,
  accent: color.accent,
  ok: color.pastel.sage,
  /** True status-green, reserved for the connection dot. */
  live: "#4AC879",
} as const;

/* --------------------------------------------------------------- layout */

const Track = styled.div`
  position: relative;
  height: 470vh;
`;

const Sticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-image: radial-gradient(
    circle,
    rgba(244, 240, 232, 0.06) 1px,
    transparent 1px
  );
  background-size: 26px 26px;
`;

const StaticShell = styled.div`
  background-image: radial-gradient(
    circle,
    rgba(244, 240, 232, 0.06) 1px,
    transparent 1px
  );
  background-size: 26px 26px;
`;

/* --------------------------------------------------------------- topbar */

const TopBar = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px clamp(16px, 2.5vw, 32px);
  border-bottom: 1px solid ${ui.border};
  font-family: ${font.mono};
  font-size: 11px;
  letter-spacing: 0.06em;
  min-width: 0;

  /* Clear the fixed nav's collapsed menu icon in the top-right corner. */
  @media (min-width: 641px) {
    padding-right: clamp(88px, 8vw, 120px);
  }
`;

const BrandDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 3px;
  background: ${ui.accent};
  box-shadow: 0 0 12px rgba(252, 174, 234, 0.65);
  flex: none;
`;

const BrandName = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${ui.text};
  white-space: nowrap;
`;

const RunChip = styled.span`
  border: 1px solid ${ui.border};
  border-radius: 999px;
  padding: 3px 10px;
  color: ${ui.muted};
  white-space: nowrap;

  @media (max-width: 640px) {
    display: none;
  }
`;

const connBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const Conn = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${ui.muted};
  white-space: nowrap;

  @media (max-width: 860px) {
    display: none;
  }
`;

const ConnDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${ui.live};
  box-shadow: 0 0 8px rgba(74, 200, 121, 0.65);
  animation: ${connBlink} 2.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Spacer = styled.span`
  flex: 1;
`;

const Clock = styled.span`
  color: ${ui.muted};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Counter = styled.span`
  color: ${ui.text};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const PTrack = styled.div`
  width: clamp(60px, 9vw, 130px);
  height: 6px;
  border-radius: 999px;
  border: 1px solid ${ui.border};
  overflow: hidden;
  flex: none;
`;

const PFill = styled(motion.div)`
  height: 100%;
  background: ${ui.accent};
  border-radius: 999px;
  transform-origin: left center;
  will-change: transform;
`;

/* ----------------------------------------------------------- main panes */

const Main = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(240px, 22vw, 300px);
  gap: clamp(10px, 1.4vw, 18px);
  padding: clamp(10px, 1.6vh, 18px) clamp(16px, 2.5vw, 32px)
    clamp(14px, 2.2vh, 24px);

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Board = styled.div`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(8px, 1vw, 12px);

  @media (max-width: 760px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

const Col = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${ui.panel};
  border: 1px solid ${ui.border};
  border-radius: 12px;
`;

const ColHead = styled.div<{ $tone?: "accent" | "ok" }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid ${ui.border};
  font-family: ${font.mono};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${(p) =>
    p.$tone === "accent" ? ui.accent : p.$tone === "ok" ? ui.ok : ui.muted};
`;

const ColCount = styled.span`
  font-weight: 500;
  border: 1px solid ${ui.border};
  border-radius: 999px;
  padding: 1px 7px;
  color: ${ui.muted};
  font-variant-numeric: tabular-nums;
`;

const ColCards = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 7px;

  @media (max-width: 760px) {
    padding: 6px;
    gap: 5px;
  }
`;

/* --------------------------------------------------------- ticket cards */

const ringPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(252, 174, 234, 0.55); }
  70% { box-shadow: 0 0 0 7px rgba(252, 174, 234, 0); }
  100% { box-shadow: 0 0 0 0 rgba(252, 174, 234, 0); }
`;

const Card = styled(motion.div)<{ $working?: boolean }>`
  position: relative;
  background: ${ui.panel2};
  border: 1px solid
    ${(p) => (p.$working ? "rgba(252, 174, 234, 0.5)" : ui.border)};
  border-radius: 9px;
  padding: 8px 10px;
  will-change: transform;

  @media (max-width: 760px) {
    padding: 6px 8px;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const WorkDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${ui.accent};
  flex: none;
  animation: ${ringPulse} 1.3s ease-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CardId = styled.span<{ $active?: boolean }>`
  font-family: ${font.mono};
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${(p) => (p.$active ? ui.accent : ui.muted)};
  flex: 1;
  min-width: 0;
  white-space: nowrap;
`;

const CardProject = styled.div`
  margin-top: 3px;
  font-family: ${font.mono};
  font-size: 9px;
  font-style: italic;
  letter-spacing: 0.04em;
  color: ${ui.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 760px) {
    display: none;
  }
`;

const CardTitle = styled.div<{ $tight?: boolean }>`
  margin-top: 4px;
  font-family: ${font.body};
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.3;
  color: rgba(244, 240, 232, 0.88);
  display: -webkit-box;
  -webkit-line-clamp: ${(p) => (p.$tight ? 1 : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;

  /* Single-line titles on phones — a half-height board must hold the
     busiest moment of the swarm without clipping. */
  @media (max-width: 760px) {
    font-size: 10.5px;
    -webkit-line-clamp: 1;
  }
`;

/* Acceptance criteria — appear when work starts, tick off in flight. */
const AcList = styled.ul`
  margin: 5px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media (max-width: 760px) {
    display: none;
  }
`;

const AcItem = styled.li<{ $done?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 5px;
  font-family: ${font.mono};
  font-size: 8.5px;
  letter-spacing: 0.04em;
  line-height: 1.45;
  color: ${(p) => (p.$done ? "rgba(244, 240, 232, 0.75)" : ui.muted)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
`;

const AcMark = styled.span<{ $done?: boolean }>`
  flex: none;
  font-weight: 700;
  color: ${(p) => (p.$done ? ui.ok : ui.muted)};
`;

const Gates = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 6px;
`;

const Gate = styled.span<{ $ok?: boolean }>`
  font-family: ${font.mono};
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  border-radius: 5px;
  padding: 2px 5px;
  border: 1px solid ${(p) => (p.$ok ? "rgba(214, 228, 210, 0.5)" : ui.border)};
  color: ${(p) => (p.$ok ? ui.ok : ui.muted)};
`;

/* ------------------------------------------------------------- done col */

const GroupHead = styled.div`
  font-family: ${font.mono};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${ui.muted};
  padding: 2px 2px 0;
  font-variant-numeric: tabular-nums;
`;

const DoneLine = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${font.mono};
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: 0.07em;
  color: ${ui.muted};
  border: 1px solid ${ui.border};
  border-radius: 7px;
  padding: 4px 8px;
  will-change: transform;
`;

const DoneCheck = styled.span`
  color: ${ui.ok};
  font-weight: 700;
  margin-left: auto;
`;

const Shipped = styled(motion.div)`
  border: 1px solid rgba(214, 228, 210, 0.55);
  background: rgba(214, 228, 210, 0.08);
  border-radius: 9px;
  padding: 9px 10px;
`;

const ShippedTop = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const ShippedName = styled.span`
  font-family: ${font.mono};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${ui.text};
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShippedTag = styled.span`
  font-family: ${font.mono};
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${ui.ok};
  border: 1px solid rgba(214, 228, 210, 0.5);
  border-radius: 5px;
  padding: 2px 5px;
  white-space: nowrap;
`;

/* ------------------------------------------------------------ feed rail */

const Rail = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.2vh, 14px);

  @media (max-width: 1100px) {
    display: none;
  }
`;

const Brain = styled.div<{ $running?: boolean }>`
  flex: 0 0 auto;
  background: ${ui.panel};
  border: 1px solid
    ${(p) => (p.$running ? "rgba(252, 174, 234, 0.5)" : ui.border)};
  border-radius: 12px;
  padding: 12px 14px;
  transition: border-color 0.3s ease;
`;

const BrainTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BrainId = styled.div`
  flex: 1;
  min-width: 0;
`;

const BrainName = styled.div`
  font-family: ${font.mono};
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${ui.text};
`;

const BrainSub = styled.div`
  font-family: ${font.mono};
  font-size: 9.5px;
  letter-spacing: 0.06em;
  color: ${ui.muted};
  margin-top: 2px;
`;

const BrainRing = styled.span<{ $on?: boolean; $done?: boolean }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: none;
  background: ${(p) => (p.$done ? ui.ok : p.$on ? ui.accent : "#4b473f")};
  animation: ${(p) => (p.$on ? ringPulse : "none")} 1.4s ease-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ------------------------------------------------- mini agent graph */

const GraphSvg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  margin-top: 10px;
`;

type GraphNode = {
  id: string;
  x: number;
  y: number;
  label: string;
  hub?: boolean;
  dashed?: boolean;
};

const GRAPH_NODES: GraphNode[] = [
  { id: "you", x: 130, y: 16, label: "you", dashed: true },
  { id: "orch", x: 130, y: 72, label: "orch", hub: true },
  { id: "pm", x: 34, y: 52, label: "pm" },
  { id: "qa", x: 226, y: 52, label: "qa" },
  { id: "vst", x: 28, y: 118, label: "vista" },
  { id: "imo", x: 79, y: 118, label: "imogen" },
  { id: "app", x: 130, y: 118, label: "ap+" },
  { id: "urb", x: 181, y: 118, label: "urbnsurf" },
  { id: "vir", x: 232, y: 118, label: "virgin" },
];

/* Edge paths double as the SMIL motion paths for the message pulses. */
const GRAPH_EDGES: { d: string; dur: number; begin: number; rev?: boolean }[] =
  [
    { d: "M 130 22 L 130 65", dur: 2.6, begin: 0.2, rev: true },
    { d: "M 34 58 Q 78 64 123 71", dur: 2.1, begin: 0 },
    { d: "M 226 58 Q 182 64 137 71", dur: 2.3, begin: 1.1, rev: true },
    { d: "M 28 112 Q 62 90 124 77", dur: 2.0, begin: 0.6 },
    { d: "M 79 112 Q 96 94 127 79", dur: 2.4, begin: 1.5, rev: true },
    { d: "M 130 112 L 130 79", dur: 1.9, begin: 0.9 },
    { d: "M 181 112 Q 164 94 133 79", dur: 2.2, begin: 0.4 },
    { d: "M 232 112 Q 198 90 136 77", dur: 2.5, begin: 1.8, rev: true },
  ];

/**
 * Minified version of the Team Orchestrator org graph — the agent
 * constellation with message pulses bouncing between the orchestrator
 * hub and its satellites while the run is live.
 */
const AgentGraph: React.FC<{ live: boolean }> = ({ live }) => (
  <GraphSvg viewBox="0 0 260 140" aria-hidden>
    {GRAPH_EDGES.map((e) => (
      <path
        key={e.d}
        d={e.d}
        fill="none"
        stroke="rgba(244, 240, 232, 0.14)"
        strokeWidth={1}
      />
    ))}
    {live
      ? GRAPH_EDGES.map((e) => (
          <circle key={`pulse-${e.d}`} r={2.2} fill={ui.accent}>
            <animateMotion
              dur={`${e.dur}s`}
              begin={`${e.begin}s`}
              repeatCount="indefinite"
              path={e.d}
              keyPoints={e.rev ? "1;0" : "0;1"}
              keyTimes="0;1"
              calcMode="linear"
            />
          </circle>
        ))
      : null}
    {GRAPH_NODES.map((n) => {
      const side = n.id === "you" || n.id === "orch";
      const above = n.id === "pm" || n.id === "qa";
      return (
        <g key={n.id}>
          <circle
            cx={n.x}
            cy={n.y}
            r={n.hub ? 5 : 3.5}
            fill={
              n.dashed
                ? "none"
                : n.hub && live
                  ? ui.accent
                  : "rgba(244, 240, 232, 0.55)"
            }
            stroke={n.dashed ? "rgba(244, 240, 232, 0.5)" : "none"}
            strokeDasharray={n.dashed ? "2 2" : undefined}
          />
          <text
            x={side ? n.x + 10 : n.x}
            y={side ? n.y + 3 : above ? n.y - 9 : n.y + 14}
            textAnchor={side ? "start" : "middle"}
            fill="rgba(244, 240, 232, 0.6)"
            fontFamily={font.mono}
            fontSize={7.5}
            letterSpacing="0.06em"
          >
            {n.label}
          </text>
        </g>
      );
    })}
  </GraphSvg>
);

const BrainMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
  padding-top: 9px;
  border-top: 1px solid ${ui.border};
  font-family: ${font.mono};
  font-size: 10px;
  letter-spacing: 0.07em;
  color: ${ui.muted};
  font-variant-numeric: tabular-nums;
`;

const Feed = styled.div`
  flex: 1;
  min-height: 0;
  background: ${ui.panel};
  border: 1px solid ${ui.border};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FeedHead = styled.div`
  flex: 0 0 auto;
  padding: 8px 12px;
  border-bottom: 1px solid ${ui.border};
  font-family: ${font.mono};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${ui.muted};
`;

const FeedList = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 5px;
  padding: 10px 12px;
  overflow: hidden;
`;

const FeedRow = styled(motion.div)<{ $accent?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 7px;
  font-family: ${font.mono};
  font-size: 10px;
  letter-spacing: 0.03em;
  color: ${(p) => (p.$accent ? ui.accent : ui.muted)};
  white-space: nowrap;
  min-width: 0;
`;

const FeedTime = styled.span`
  flex: none;
  opacity: 0.65;
  font-variant-numeric: tabular-nums;
`;

const FeedRoute = styled.span`
  flex: none;
  color: inherit;
`;

const FeedSubject = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* -------------------------------------------------------------- end beat */

const arrowBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
`;

const HintArrow = styled.span`
  display: inline-block;
  margin-left: 8px;
  color: ${ui.accent};
  animation: ${arrowBounce} 1.5s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const EndHint = styled(motion.div)`
  position: absolute;
  left: 50%;
  bottom: clamp(18px, 3.5vh, 34px);
  transform: translateX(-50%);
  font-family: ${font.mono};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${ui.text};
  background: rgba(14, 14, 12, 0.85);
  border: 1px solid ${ui.borderStrong};
  border-radius: 999px;
  padding: 8px 18px;
  white-space: nowrap;
  pointer-events: none;

  /* The mobile nav lives bottom-centre — keep the hint clear of it. */
  @media (max-width: 640px) {
    bottom: 72px;
  }
`;

/* ------------------------------------------------------------ the board */

const COLUMN_LABELS = ["Backlog", "In progress", "Review", "Done"] as const;

const layoutSpring = { duration: 0.55, ease: ease.out };

const TicketCard: React.FC<{ t: Ticket; state: 0 | 1 | 2; p: number }> = ({
  t,
  state,
  p,
}) => (
  <Card
    layoutId={t.key}
    layout
    $working={state === 1}
    initial={false}
    transition={{ layout: layoutSpring }}
  >
    <CardTop>
      {state === 1 ? <WorkDot /> : null}
      <CardId $active={state === 1}>{t.key}</CardId>
      <AnimatedIcon
        name={t.eng.card.project.icon}
        size={11}
        strokeWidth={2.2}
        color={state === 1 ? ui.accent : ui.muted}
        active={state >= 1}
      />
    </CardTop>
    <CardProject>{t.eng.desc}</CardProject>
    <CardTitle $tight={state === 1}>{t.title}</CardTitle>
    {state >= 1 ? (
      <AcList aria-label="Acceptance criteria">
        {t.ac.map((a, i) => {
          const ticked = p >= t.acAt[i];
          return (
            <AcItem key={a} $done={ticked}>
              <AcMark $done={ticked} aria-hidden>
                {ticked ? "✓" : "○"}
              </AcMark>
              {a}
            </AcItem>
          );
        })}
      </AcList>
    ) : null}
    {state === 2 ? (
      <Gates>
        <Gate $ok={p >= t.crAt}>CR {p >= t.crAt ? "✓" : "•"}</Gate>
        <Gate $ok={p >= t.qaAt}>QA {p >= t.qaAt ? "✓" : "•"}</Gate>
      </Gates>
    ) : null}
  </Card>
);

const DoneColumn: React.FC<{ done: Ticket[]; p: number; instant: boolean }> = ({
  done,
  p,
  instant,
}) => (
  <>
    {ENGAGEMENTS.map((eng) => {
      const id = eng.card.project.id;
      const mine = done.filter((t) => t.eng === eng);
      if (p >= SHIP_AT[id]) {
        return (
          <Shipped
            key={id}
            initial={instant ? false : { opacity: 0, scale: 0.86 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: ease.out }}
          >
            <ShippedTop>
              <AnimatedIcon
                name={eng.card.project.icon}
                size={12}
                strokeWidth={2.2}
                color={ui.ok}
                active
              />
              <ShippedName>{eng.short}</ShippedName>
              <ShippedTag>shipped ✓</ShippedTag>
            </ShippedTop>
          </Shipped>
        );
      }
      if (mine.length === 0) return null;
      return (
        <React.Fragment key={id}>
          <GroupHead>
            {eng.tag} {mine.length}/4
          </GroupHead>
          {mine.map((t) => (
            <DoneLine
              key={t.key}
              layoutId={t.key}
              layout
              initial={false}
              transition={{ layout: layoutSpring }}
            >
              {t.key}
              <DoneCheck>✓</DoneCheck>
            </DoneLine>
          ))}
        </React.Fragment>
      );
    })}
  </>
);

/* In static mode, `?p=0.35` freezes the board mid-run — used for
   screenshot verification of in-flight states. Defaults to 1 (all done). */
const staticProgress = (): number => {
  if (typeof window === "undefined") return 1;
  const raw = new URLSearchParams(window.location.search).get("p");
  const n = raw === null ? 1 : Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 1;
};

const MissionBoard: React.FC = () => {
  const reduced = useReducedMotion();
  const instant = STATIC_MODE || !!reduced;
  const trackRef = React.useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });

  const [step, setStep] = useState(instant ? THRESHOLDS.length : 0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    if (instant) return;
    const s = stepFor(v);
    setStep((prev) => (prev === s ? prev : s));
  });

  const p = instant ? staticProgress() : progressAt(step);

  const byCol: Ticket[][] = [[], [], [], []];
  TICKETS.forEach((t) => {
    const s = stateOf(t, p);
    if (s >= 0) byCol[s].push(t);
  });
  const doneCount = byCol[3].length;
  const shippedCount = Object.values(SHIP_AT).filter((v) => p >= v).length;
  const running = byCol[1].length > 0;
  const runDone = p >= RUN_DONE;
  const feedItems = EVENTS.filter((e) => e.t <= p).slice(-16);

  const shell = (
    <>
      <TopBar>
        <BrandDot />
        <BrandName>Orchestrator</BrandName>
        <RunChip>run("senior_era")</RunChip>
        <Conn>
          <ConnDot />
          connected
        </Conn>
        <Spacer />
        <Clock>t+{fmtClock(p)}</Clock>
        <Counter>{doneCount}/20 done</Counter>
        <PTrack>
          <PFill style={{ scaleX: instant ? p : scrollYProgress }} />
        </PTrack>
      </TopBar>
      <Main>
        <LayoutGroup>
          <Board>
            {COLUMN_LABELS.map((label, colIndex) => (
              <Col key={label}>
                <ColHead
                  $tone={
                    colIndex === 1
                      ? "accent"
                      : colIndex === 3
                        ? "ok"
                        : undefined
                  }
                >
                  <span>{label}</span>
                  <ColCount>
                    {colIndex === 3 ? doneCount : byCol[colIndex].length}
                  </ColCount>
                </ColHead>
                <ColCards>
                  {colIndex === 3 ? (
                    <DoneColumn done={byCol[3]} p={p} instant={instant} />
                  ) : (
                    byCol[colIndex].map((t) => (
                      <TicketCard
                        key={t.key}
                        t={t}
                        state={colIndex as 0 | 1 | 2}
                        p={p}
                      />
                    ))
                  )}
                </ColCards>
              </Col>
            ))}
          </Board>
        </LayoutGroup>
        <Rail>
          <Brain $running={running && !runDone}>
            <BrainTop>
              <AnimatedIcon
                name="brain"
                size={17}
                strokeWidth={1.8}
                color={ui.text}
                active={running || runDone}
              />
              <BrainId>
                <BrainName>Orchestrator</BrainName>
                <BrainSub>multi-model · high effort</BrainSub>
              </BrainId>
              <BrainRing $on={running && !runDone} $done={runDone} />
            </BrainTop>
            <AgentGraph live={!instant && running && !runDone} />
            <BrainMeta>
              <span>{doneCount}/20 tickets</span>
              <span>{shippedCount}/5 shipped</span>
            </BrainMeta>
          </Brain>
          <Feed>
            <FeedHead>Live feed</FeedHead>
            <FeedList aria-hidden>
              {feedItems.map((e) => (
                <FeedRow
                  key={`${e.t}-${e.subject}`}
                  $accent={e.accent}
                  initial={instant ? false : { opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: ease.out }}
                >
                  <FeedTime>{fmtClock(e.t)}</FeedTime>
                  <FeedRoute>
                    {e.from} → {e.to}
                  </FeedRoute>
                  <FeedSubject>{e.subject}</FeedSubject>
                </FeedRow>
              ))}
            </FeedList>
          </Feed>
        </Rail>
      </Main>
      {!instant ? (
        <EndHint
          initial={false}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          transition={{ duration: 0.5, ease: ease.out }}
        >
          {runDone
            ? "run complete — compiling engagements"
            : "keep scrolling — the run is live"}
          <HintArrow aria-hidden>↓</HintArrow>
        </EndHint>
      ) : null}
    </>
  );

  if (instant) {
    return <StaticShell>{shell}</StaticShell>;
  }

  return (
    <Track ref={trackRef}>
      <Sticky>{shell}</Sticky>
    </Track>
  );
};

export default MissionBoard;
